import { useEffect, useState } from 'react';
import { FileSpreadsheet, FileText, FileX, Loader2, Paperclip } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { fileExtension, formatFileSize, hasFile } from './documentsModel';
import { blobBytes, blobText, pdfCaption, previewKind, readCsvPreview, readPdfInfo } from './fileInfo';

/** Files above this are not fetched just for a thumbnail; the preview offers a button instead. */
export const PREVIEW_MAX_BYTES = 12 * 1024 * 1024;
const MIME = { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif' };

const apiPath = (fileUrl) => (String(fileUrl || '').startsWith('/api/') ? String(fileUrl).slice(4) : String(fileUrl || ''));

/**
 * Loads the current file of a document once and reads what the preview needs from it (16 idea 7). The blob gets
 * the type of the file's extension only (PDF / image) — anything else is never put in an <object> or <img>.
 */
function useFilePreview(doc, enabled) {
  const [state, setState] = useState({ status: 'idle', url: '', info: null, csv: null, error: '' });
  const kind = previewKind(doc);
  const fileUrl = doc?.file_url || '';
  const tooBig = Number(doc?.file_size) > PREVIEW_MAX_BYTES;

  useEffect(() => {
    if (!enabled || !fileUrl || kind === 'other' || tooBig) {
      setState({ status: 'idle', url: '', info: null, csv: null, error: '' });
      return undefined;
    }
    let cancelled = false;
    let objectUrl = '';
    setState({ status: 'loading', url: '', info: null, csv: null, error: '' });
    (async () => {
      try {
        const response = await api.get(apiPath(fileUrl), { responseType: 'blob' });
        const blob = response.data;
        if (cancelled) return;
        if (kind === 'csv') {
          const text = await blobText(blob);
          if (!cancelled) setState({ status: 'ready', url: '', info: { size: blob.size }, csv: readCsvPreview(text), error: '' });
          return;
        }
        const ext = fileExtension(doc).toLowerCase();
        const typed = new Blob([blob], { type: MIME[ext] || 'application/octet-stream' });
        let info = { size: blob.size };
        if (kind === 'pdf') {
          info = { ...info, ...(readPdfInfo(await blobBytes(blob)) || {}) };
        }
        if (cancelled) return;
        objectUrl = URL.createObjectURL(typed);
        setState({ status: 'ready', url: objectUrl, info, csv: null, error: '' });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', url: '', info: null, csv: null, error: getErrorMessage(err, 'Could not load the preview') });
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [enabled, fileUrl, kind, tooBig]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...state, kind, tooBig };
}

/** A drawing-sheet look for files the browser cannot draw here (and while a PDF loads). */
function BlankSheet({ label, icon: Icon = FileText, tone = 'navy' }) {
  return (
    <span className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[color:var(--surface)]">
      <span
        className="absolute inset-0 opacity-70"
        style={{
          backgroundImage: 'linear-gradient(var(--line) 1px, transparent 1px), linear-gradient(90deg, var(--line) 1px, transparent 1px)',
          backgroundSize: '14px 14px',
        }}
        aria-hidden="true"
      />
      <span className={cn('ui-icon-tile relative h-10 w-10 rounded-xl', `tone-${tone}`)} aria-hidden="true">
        <Icon className="h-5 w-5" />
      </span>
      {label ? (
        <span className="absolute bottom-1.5 right-1.5 rounded-[3px] border border-line-2 bg-surface px-1 font-mono text-[8px] font-semibold uppercase leading-[13px] tracking-[0.04em] text-ink-3">
          {label}
        </span>
      ) : null}
    </span>
  );
}

function CsvSheet({ csv }) {
  const [head, ...rest] = csv.rows;
  if (!head) return <BlankSheet label="CSV" icon={FileSpreadsheet} tone="green" />;
  return (
    <span className="block h-full w-full overflow-hidden bg-surface p-1.5 text-left">
      <table className="w-full table-fixed border-collapse text-[8.5px] leading-[13px] text-ink-2">
        <thead>
          <tr>
            {head.map((cell, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <th key={index} className="truncate border border-line bg-surface-2 px-1 text-left font-bold text-ink-3">{cell}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rest.map((row, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={rowIndex}>
              {head.map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index} className="truncate border border-line px-1">{row[index] || ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </span>
  );
}

/**
 * The first page of the current file inside the expanded row (16 idea 7): the PDF's own first page (the
 * browser's PDF viewer, no toolbar), the image itself, or the first rows of a CSV — with "PDF · 2.4 MB · A1 ·
 * 1 sheet" under it. Clicking it opens the file. Excel / Word files say why there is no preview.
 */
export default function DocumentPreview({ doc, canEdit, onOpen, onDownload, onAttach }) {
  const [wanted, setWanted] = useState(false);
  const [imageSize, setImageSize] = useState(null);
  useEffect(() => setImageSize(null), [doc?.file_url]);
  const enabled = hasFile(doc) && (Number(doc?.file_size) <= PREVIEW_MAX_BYTES || wanted);
  const preview = useFilePreview(doc, enabled);
  const ext = fileExtension(doc);

  if (!hasFile(doc)) {
    return (
      <div className="flex flex-col items-start gap-2 rounded-[10px] border border-dashed border-[color:var(--status-amber)] bg-surface p-3" data-testid={`document-preview-${doc.id}`}>
        <span className="ui-icon-tile tone-amber h-8 w-8 rounded-lg" aria-hidden="true"><FileX className="h-4 w-4" /></span>
        <p className="text-[12.5px] font-semibold text-status-amber-ink">No file attached</p>
        <p className="text-[11.5px] leading-snug text-ink-3">Only the details of v{doc.version || 1} are registered.</p>
        {canEdit ? (
          <Button type="button" size="xs" variant="outline" onClick={() => onAttach(doc)}>
            <Paperclip aria-hidden="true" />
            Attach file
          </Button>
        ) : null}
      </div>
    );
  }

  const size = formatFileSize(doc.file_size || preview.info?.size);
  let detail = '';
  if (preview.kind === 'pdf') detail = pdfCaption(preview.info, doc.type);
  else if (preview.kind === 'image' && imageSize) detail = `${imageSize.width} × ${imageSize.height} px`;
  else if (preview.kind === 'csv' && preview.csv) detail = `${preview.csv.total} ${preview.csv.total === 1 ? 'row' : 'rows'}`;
  else if (preview.kind === 'other') detail = 'no preview — download to open';
  const caption = [ext, size, detail].filter(Boolean).join(' · ');
  const openable = preview.kind === 'pdf' || preview.kind === 'image';
  const open = () => (openable ? onOpen(doc) : onDownload(doc));

  let body;
  if (preview.tooBig && !wanted) {
    body = (
      <span className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-surface text-[11.5px] text-ink-3">
        <FileText className="h-5 w-5" aria-hidden="true" />
        Large file
      </span>
    );
  } else if (preview.status === 'loading') {
    body = (
      <span className="flex h-full w-full items-center justify-center bg-surface text-ink-3">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading the preview</span>
      </span>
    );
  } else if (preview.status === 'error') {
    body = <BlankSheet label={ext} />;
  } else if (preview.kind === 'image' && preview.url) {
    body = (
      <img
        src={preview.url}
        alt=""
        className="h-full w-full bg-surface object-contain"
        onLoad={(event) => {
          const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
          if (width) setImageSize({ width, height });
        }}
      />
    );
  } else if (preview.kind === 'pdf' && preview.url) {
    body = (
      // The browser's own PDF viewer draws page 1 (no toolbar). Browsers without an inline viewer show the sheet.
      <object
        data={`${preview.url}#page=1&view=Fit&toolbar=0&navpanes=0&scrollbar=0`}
        type="application/pdf"
        aria-label={`First page of ${doc.title}`}
        // a few px larger than the box: the viewer's grey page margin falls outside the rounded frame
        className="pointer-events-none absolute -left-1 -top-1 h-[calc(100%+8px)] w-[calc(100%+8px)] bg-surface"
        tabIndex={-1}
      >
        <BlankSheet label={[pdfCaption(preview.info, doc.type).split(' · ')[0], `v${doc.version || 1}`].filter(Boolean).join(' · ')} />
      </object>
    );
  } else if (preview.kind === 'csv' && preview.csv) {
    body = <CsvSheet csv={preview.csv} />;
  } else {
    body = <BlankSheet label={ext} icon={['XLS', 'XLSX'].includes(ext) ? FileSpreadsheet : FileText} tone={['XLS', 'XLSX'].includes(ext) ? 'green' : 'navy'} />;
  }

  return (
    <figure className="m-0 w-[176px] max-w-full" data-testid={`document-preview-${doc.id}`} data-preview-kind={preview.kind} data-preview-status={preview.status}>
      <div className="relative h-[124px] w-full overflow-hidden rounded-lg border border-line-2 bg-surface shadow-card">
        {body}
        <button
          type="button"
          onClick={open}
          className="absolute inset-0 rounded-lg outline-none transition-colors hover:bg-[color:var(--brand-soft)]/20 focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={openable ? `Open ${doc.title} v${doc.version || 1}` : `Download ${doc.title} v${doc.version || 1}`}
          title={openable ? 'Open' : 'Download'}
          data-testid={`document-preview-open-${doc.id}`}
        />
      </div>
      <figcaption className="mt-1.5 text-[11.5px] leading-snug text-ink-3" data-testid={`document-preview-caption-${doc.id}`}>
        {caption}
        {preview.tooBig && !wanted ? (
          <>
            {' · '}
            <button type="button" className="font-semibold text-brand-ink hover:underline" onClick={() => setWanted(true)}>Show preview</button>
          </>
        ) : null}
      </figcaption>
    </figure>
  );
}
